/**
 * Login page — two-step when MFA is enabled.
 *
 * Step 1 (credentials): username + password → POST /api/auth/login
 *   - If mfa_required=false → tokens returned, navigate to app
 *   - If mfa_required=true  → switch to step 2
 *
 * Step 2 (mfa): 6-digit TOTP code → POST /api/auth/login/mfa
 *   - On success → tokens returned, navigate to app
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import { ApiError } from '../api/client.js';
import './LoginPage.css';
import { usePageTitle } from '../hooks/usePageTitle.js';


export default function LoginPage() {
  usePageTitle('Sign in');

  const { login, loginMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If user was bounced here from a protected route, remember where.
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('next') || location.state?.from?.pathname || '/';

  // ── shared state ──
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // ── step 1: credentials ──
  const [step, setStep] = useState('credentials');     // 'credentials' | 'mfa'
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ── step 2: mfa ──
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const canSubmitCreds = usernameOrEmail.trim().length > 0 && password.length > 0 && !submitting;
  const canSubmitMfa = mfaCode.length === 6 && !submitting;

  // ── step 1 handler ──
  async function handleCredentials(e) {
    e.preventDefault();
    if (!canSubmitCreds) return;

    setSubmitting(true);
    setError('');

    try {
      const result = await login(usernameOrEmail.trim(), password);

      if (result?.mfa_required) {
        // Password was correct — now we need the TOTP code
        setMfaToken(result.mfa_token);
        setStep('mfa');
        setPassword('');           // clear password from memory
      } else {
        // No MFA — we're in
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Wrong username or password. Try again or text Tim.');
      } else {
        setError('Something went wrong. Check your connection and try again.');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  // ── step 2 handler ──
  async function handleMfa(e) {
    e.preventDefault();
    if (!canSubmitMfa) return;

    setSubmitting(true);
    setError('');

    try {
      await loginMfa(mfaToken, mfaCode);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        const detail = err.detail || '';
        if (detail.includes('expired')) {
          // mfa_pending token timed out — back to password
          setError('MFA session expired. Please sign in again.');
          setStep('credentials');
          setMfaToken('');
        } else {
          setError('Invalid code. Check your authenticator app and try again.');
        }
      } else {
        setError('Something went wrong. Check your connection and try again.');
      }
      setMfaCode('');
    } finally {
      setSubmitting(false);
    }
  }

  // ── back to credentials ──
  function handleBack() {
    setStep('credentials');
    setMfaToken('');
    setMfaCode('');
    setError('');
  }

  return (
    <div className="login-page">
      <header className="login-top" aria-hidden="true" />

      <main className="login-stage">
        <section className="login-card">

          {step === 'credentials' ? (
            /* ═══════════════ STEP 1: credentials ═══════════════ */
            <>
              <div className="login-card-head">
                <span className="login-sticker">🔒 members only</span>
                <h1 className="login-title">Welcome back</h1>
                <p className="login-sub">sign in to keep things interesting</p>
              </div>

              {error && (
                <div className="login-error" role="alert">
                  <span aria-hidden="true">⚠</span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleCredentials} noValidate>
                <div className="login-field">
                  <label className="login-label" htmlFor="login-ident">
                    <span>username</span>
                  </label>
                  <input
                    id="login-ident"
                    type="text"
                    autoComplete="username"
                    placeholder="your username or email"
                    value={usernameOrEmail}
                    onChange={(e) => setUsernameOrEmail(e.target.value)}
                    autoFocus
                  />
                </div>

                <div className="login-field">
                  <label className="login-label" htmlFor="login-pw">
                    <span>password</span>
                    <Link
                      to="/forgot"
                      onClick={(e) => {
                        e.preventDefault();
                        alert('Not ready yet — text Tim for a reset.');
                      }}
                    >
                      forgot it?
                    </Link>
                  </label>
                  <div className="login-input-wrap">
                    <input
                      id="login-pw"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="login-pw-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? 'hide' : 'show'}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-submit"
                  disabled={!canSubmitCreds}
                >
                  {submitting ? 'signing in…' : 'sign in  →'}
                </button>
              </form>

              <div className="login-divider">or</div>

              <Link to="/request-invite" className="login-request-invite">
                don't have an account?
                <strong>request an invite →</strong>
              </Link>
            </>
          ) : (
            /* ═══════════════ STEP 2: MFA code ═══════════════ */
            <>
              <div className="login-card-head">
                <span className="login-sticker">🔐 verification required</span>
                <h1 className="login-title">One more step</h1>
                <p className="login-sub">enter the code from your authenticator app</p>
              </div>

              {error && (
                <div className="login-error" role="alert">
                  <span aria-hidden="true">⚠</span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleMfa} noValidate>
                <div className="login-field">
                  <label className="login-label" htmlFor="mfa-code">
                    <span>6-digit code</span>
                  </label>
                  <input
                    id="mfa-code"
                    className="login-mfa-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setMfaCode(v);
                    }}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="login-submit"
                  disabled={!canSubmitMfa}
                >
                  {submitting ? 'verifying…' : 'verify  →'}
                </button>
              </form>

              <button
                type="button"
                className="login-back-btn"
                onClick={handleBack}
              >
                ← back to sign in
              </button>
            </>
          )}

        </section>
      </main>

      <div className="login-tagline">
        <strong>NachoFiles</strong> — the reason the chicken crossed the road
      </div>
    </div>
  );
}
